/**
 * @file GenerateReportPageSkeleton.tsx
 *
 * @description Provides a loading skeleton placeholder for the Report Generation page.
 * This component is displayed during data loading to improve perceived performance
 * and reduce layout shifts when the actual content renders.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import { Box, Skeleton, Card, CardContent } from "@mui/material";
import Grid from "@mui/material/Grid";

/**
 * @function GenerateReportPageSkeleton
 *
 * @description This component creates placeholder UI elements that mimic the structure of the
 * actual Report Generation page, giving officers a preview of the layout while
 * data is being loaded. The skeleton includes:
 *
 * - Main content area with:
 *   - Left panel containing video evidence placeholder and metadata fields
 *   - Right panel containing report generation controls
 *
 * @returns {JSX.Element} A skeleton placeholder UI for the Generate Report page
 */
export const GenerateReportPageSkeleton: React.FC = () => {
  return (
    <Grid
      container
      spacing={3}
      direction="column"
      sx={{ width: "50vw", maxWidth: "60vw" }}
    >
      {/* Main container skeleton */}
      <Grid
        size={{ xs: 12 }}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 2,
          width: "100%",
        }}
      >
        {/* Header Section */}
        <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Skeleton variant="circular" width={50} height={50} />
            <Skeleton variant="text" width={300} height={50} />
          </Box>
        </Grid>

        {/* Main Content */}
        <Grid container spacing={2} sx={{ width: "100%" }}>
          {/* Left Side - Video evidence and metadata */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Card elevation={3} sx={{ width: "100%" }}>
              <CardContent>
                {/* Video section header with action button */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  {/* Video title placeholder */}
                  <Skeleton variant="text" width={150} height={28} />
                  {/* Action button placeholder */}
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
                {/* Video - 16:9 aspect ratio */}
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  sx={{
                    paddingTop: "60%",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                />
                {/* Metadata fields section */}
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{
                        backgroundColor: "action.hover",
                        p: 2,
                        borderRadius: 1,
                        height: "100%",
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton variant="text" width="80%" height={24} />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box
                      sx={{
                        backgroundColor: "action.hover",
                        p: 2,
                        borderRadius: 1,
                        height: "100%",
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton variant="text" width="80%" height={24} />
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box
                      sx={{
                        backgroundColor: "action.hover",
                        p: 2,
                        borderRadius: 1,
                      }}
                    >
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ mb: 1 }}
                      />
                      <Skeleton variant="text" width="60%" height={24} />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Panel - Report generation controls */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card
              elevation={3}
              sx={{
                height: "100%",
                minHeight: "400px",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <CardContent
                sx={{ flex: 1, display: "flex", flexDirection: "column" }}
              >
                {/* Report type selection header */}
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}
                >
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={180} height={32} />
                </Box>

                {/* Report template dropdown placeholder */}
                <Skeleton variant="rectangular" height={56} sx={{ mb: 1 }} />
                <Skeleton
                  variant="text"
                  width="100%"
                  height={16}
                  sx={{ mb: 1 }}
                />
                <Skeleton
                  variant="text"
                  width="100%"
                  height={16}
                  sx={{ mb: 1 }}
                />

                <Skeleton
                  variant="rectangular"
                  width={120}
                  height={36}
                  sx={{ mt: 1, mb: 3 }}
                />

                <Box sx={{ flex: 1 }} />

                {/* Button skeleton */}
                <Skeleton
                  variant="rectangular"
                  height={56}
                  sx={{ borderRadius: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
